import { BaseExercise } from "@domain/exercise/exercise";
import { AnalyticsViewExerciseDetailOrigin } from "@domain/library/exercise-analytics";
import { ResponsiveDeviceType } from "@ui/hooks/use-dimensions";
import { AlternativeExerciseFooterOptions } from "@ui/pages/program-builder/alternative-exercise/alternative-exercise-footer-options";
import { AlternativeExerciseOptions } from "@ui/pages/program-builder/alternative-exercise/alternative-exercise-options";
import { ExerciseDetailsDialogTabs } from "@ui/pages/program-builder/dialog/exercise-details-dialog";
import { useEditorExerciseCardActions } from "@ui/pages/program-builder/hooks/use-editor-exercise-card-actions";
import { DraggableCard } from "@ui/pages/program-builder/library/draggable-card";
import { ExerciseCard } from "@ui/pages/program-builder/library/exercise-card";
import { HTMLMotionProps } from "framer-motion";
import React from "react";
import { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd";

type ExerciseCardStyle = "row" | "card";

interface AlternativeExerciseCardProps {
	alternatives: BaseExercise[];
	alternativeExercise: BaseExercise;
	alternativeExerciseIndex: number;
	relatedExercises: BaseExercise[];
	updateExercise: (newExercise: BaseExercise) => void;
	onDelete: (exercise: BaseExercise) => void;
	className?: string;
	shouldAnimate?: boolean;
	draggable?: boolean;
	isDragging?: boolean;
	deviceType?: ResponsiveDeviceType.Desktop | ResponsiveDeviceType.Mobile;
	uiStyle?: ExerciseCardStyle;
	onReorder?: (sourceAlternativeExerciseIndex: number, destinationAlternativeExerciseIndex: number) => void;
}

export const DraggableAlternativeExerciseCard: React.FC<AlternativeExerciseCardProps & { index: number }> = ({
	alternatives,
	alternativeExercise,
	alternativeExerciseIndex,
	relatedExercises,
	uiStyle,
	shouldAnimate,
	className,
	onReorder,
	updateExercise,
	onDelete,
	index,
}) => (
	<DraggableCard
		exercise={alternativeExercise}
		index={index}
		renderCard={({ cardStyle, overriddenUiStyle, provided, snapshot }) => (
			<AlternativeExerciseCard
				ref={provided.innerRef}
				{...provided.draggableProps}
				{...(provided.dragHandleProps as DragHandleProps | undefined)}
				className={className}
				style={cardStyle}
				shouldAnimate={shouldAnimate}
				alternativeExercise={alternativeExercise}
				relatedExercises={relatedExercises}
				uiStyle={overriddenUiStyle ?? uiStyle}
				id={alternativeExercise.draggableId}
				draggable={true}
				isDragging={snapshot.isDragging}
				onDelete={onDelete}
				updateExercise={updateExercise}
				onReorder={onReorder}
				alternatives={alternatives}
				alternativeExerciseIndex={alternativeExerciseIndex}
				deviceType={ResponsiveDeviceType.Desktop}
			/>
		)}
	/>
);

export const AlternativeExerciseCard = React.forwardRef<
	HTMLDivElement,
	AlternativeExerciseCardProps & HTMLMotionProps<"div">
>(
	(
		{
			uiStyle = "card",
			deviceType = ResponsiveDeviceType.Mobile,
			alternatives,
			alternativeExercise,
			alternativeExerciseIndex,
			relatedExercises,
			shouldAnimate,
			draggable = false,
			isDragging = false,
			onReorder,
			onDelete,
			updateExercise,
			...props
		},
		ref,
		// eslint-disable-next-line sonarjs/cognitive-complexity
	) => {
		const { openExerciseDetailsModal } = useEditorExerciseCardActions();

		return (
			<ExerciseCard
				{...props}
				ref={ref}
				uiStyle={uiStyle}
				deviceType={deviceType}
				exercise={alternativeExercise}
				shouldAnimate={shouldAnimate}
				draggable={draggable}
				isDragging={isDragging}
				onClick={() =>
					openExerciseDetailsModal(alternativeExercise, relatedExercises, AnalyticsViewExerciseDetailOrigin.CANVAS)
				}
				onCommentIconClick={() =>
					openExerciseDetailsModal(
						alternativeExercise,
						relatedExercises,
						AnalyticsViewExerciseDetailOrigin.CANVAS,
						ExerciseDetailsDialogTabs.COMMENT,
					)
				}
				Options={
					<AlternativeExerciseOptions
						alternativeExercise={alternativeExercise}
						onDelete={onDelete}
						relatedExercises={relatedExercises}
					/>
				}
				Footer={
					<AlternativeExerciseFooterOptions
						alternatives={alternatives}
						alternativeExercise={alternativeExercise}
						alternativeExerciseIndex={alternativeExerciseIndex}
						relatedExercises={relatedExercises}
						onDelete={onDelete}
						onReorder={onReorder}
					/>
				}
				updateExercise={updateExercise}
			/>
		);
	},
);
AlternativeExerciseCard.displayName = "AlternativeExerciseCard";

type DragHandleProps = DraggableProvidedDragHandleProps & { onDragStart: HTMLMotionProps<"div">["onDragStart"] };

